/**
 * Cost Tracking Test Runner
 *
 * Tests cost tracking across PiPar backend endpoints.
 * Modelled after backend/test_tool/cost_test_client.py.
 *
 * Each test:
 *  1. Records session costs before the call
 *  2. Calls the endpoint
 *  3. Records session costs after
 *  4. Verifies cost_info was returned (inline or via delta)
 *
 * All tests share a single session so cumulative costs can be checked.
 */

import type { ChatEvent } from '../types'
import { startChat, stopChat, sendMessage, getSessionCosts, updateMemory } from './api'
import { getIdToken } from './auth'
import { API_BASE } from './backendConfig'

// ── Types ──────────────────────────────────────────────────────

export interface CostTestStep {
  label: string
  action: (ctx: CostTestContext) => Promise<CostStepOutcome>
}

export interface CostStepOutcome {
  success: boolean
  detail: string
  costInfo?: Record<string, unknown> | null
  costDelta?: Record<string, unknown> | null
  responseTime?: number
}

export interface CostTestResult {
  name: string
  passed: boolean
  steps: {
    label: string
    passed: boolean
    detail: string
    costInfo?: Record<string, unknown> | null
    costDelta?: Record<string, unknown> | null
    responseTime?: number
    duration: number
  }[]
  duration: number
  error?: string
  /** Cumulative session costs at the end of the test set */
  sessionCosts?: Record<string, unknown> | null
}

export type CostTestProgressCallback = (
  setName: string,
  stepIndex: number,
  totalSteps: number,
  status: 'running' | 'passed' | 'failed',
  detail?: string,
) => void

interface CostTestContext {
  voiceChatId: string
  apiBase: string
}

export interface CostTestSet {
  name: string
  description: string
  steps: CostTestStep[]
}

// ── Helpers ────────────────────────────────────────────────────

async function authHeaders(contentType = 'application/json'): Promise<Record<string, string>> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
  if (contentType) headers['Content-Type'] = contentType
  return headers
}

/** Collect text and cost_info from an SSE chat stream. */
async function collectStreamCost(
  voiceChatId: string,
  message: string,
  model: string,
): Promise<{ text: string; costInfo: Record<string, unknown> | null; eventCount: number }> {
  let text = ''
  let costInfo: Record<string, unknown> | null = null
  let eventCount = 0

  for await (const event of sendMessage(voiceChatId, message, model)) {
    eventCount++
    if (event.type === 'text' || event.type === 'content') {
      text += (typeof event.data === 'string' ? event.data : '') || ''
    }
    if (event.type === 'complete') {
      const d = event.data as Record<string, unknown> | undefined
      if (d?.cost_info) costInfo = d.cost_info as Record<string, unknown>
    }
    if (event.type === 'error') {
      console.error(`  [stream_error] ${event.data}`)
    }
  }

  return { text, costInfo, eventCount }
}

/** Call /completions (non-streaming) and return cost_info. */
async function callCompletions(
  voiceChatId: string,
  model: string,
  message: string,
): Promise<{ success: boolean; costInfo: Record<string, unknown> | null; usage: Record<string, unknown> | null; responseTime: number }> {
  const headers = await authHeaders()
  const start = performance.now()

  const res = await fetch(`${API_BASE}/completions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model,
      voice_chat_id: voiceChatId,
      messages: [
        { role: 'system', content: 'Reply in one word.' },
        { role: 'user', content: message },
      ],
      stream: false,
    }),
  })

  const responseTime = performance.now() - start
  if (!res.ok) {
    const err = await res.text()
    console.error(`  /completions error: ${res.status} ${err.slice(0, 200)}`)
    return { success: false, costInfo: null, usage: null, responseTime }
  }

  const data = await res.json()
  return {
    success: true,
    costInfo: data.cost_info || null,
    usage: data.usage || null,
    responseTime,
  }
}

/** Call /tts and return response info. */
async function callTts(
  voiceChatId: string,
  text: string,
): Promise<{ success: boolean; audioBytes: number; responseTime: number; error?: string }> {
  const headers = await authHeaders()
  const start = performance.now()

  try {
    const res = await fetch(`${API_BASE}/tts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        chat_id: voiceChatId,
        service: 'vertex',
        model: 'gemini-2.5-pro-tts',
        format: 'mp3',
        instructions: 'Speak naturally.',
        speed: 1.0,
      }),
    })

    const responseTime = performance.now() - start
    if (!res.ok) {
      const err = await res.text()
      console.error(`  /tts error: ${res.status} ${err.slice(0, 200)}`)
      return { success: false, audioBytes: 0, responseTime, error: `HTTP ${res.status}: ${err.slice(0, 100)}` }
    }

    const blob = await res.blob()
    return { success: true, audioBytes: blob.size, responseTime }
  } catch (e) {
    const responseTime = performance.now() - start
    return { success: false, audioBytes: 0, responseTime, error: String(e) }
  }
}

/** Call /web_search and return cost_info. */
async function callWebSearch(
  voiceChatId: string,
  query: string,
  searchType = 'google_search',
): Promise<{ success: boolean; costInfo: Record<string, unknown> | null; responseTime: number }> {
  const headers = await authHeaders()
  const start = performance.now()

  const res = await fetch(`${API_BASE}/web_search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query,
      model: 'gemini-3-flash',
      voice_chat_id: voiceChatId,
      search_type: searchType,
    }),
  })

  const responseTime = performance.now() - start
  if (!res.ok) {
    const err = await res.text()
    console.error(`  /web_search error: ${res.status} ${err.slice(0, 200)}`)
    return { success: false, costInfo: null, responseTime }
  }

  const data = await res.json()
  return { success: true, costInfo: data.cost_info || null, responseTime }
}

/** Call /voice_chat/update_memory and return cost. */
async function callUpdateMemory(
  voiceChatId: string,
): Promise<{ success: boolean; cost: number; responseTime: number }> {
  const start = performance.now()
  try {
    const result = await updateMemory({
      discussionId: `cost_test_${Date.now()}`,
      messageHistory: [
        { role: 'user', content: 'My name is Test User and I like programming.' },
        { role: 'assistant', content: 'Nice to meet you! Programming is great.' },
      ],
      personaName: 'pipar',
      personaId: 'pipar',
    })
    const responseTime = performance.now() - start
    return { success: Boolean(result.success), cost: (result.cost as number) || 0, responseTime }
  } catch (e) {
    return { success: false, cost: 0, responseTime: performance.now() - start }
  }
}

// ── Test Sets ──────────────────────────────────────────────────

function chatCostTests(): CostTestSet {
  return {
    name: 'Chat Streaming Costs',
    description: 'Verify cost_info in /chat SSE stream for different models',
    steps: [
      {
        label: 'Chat (gemini-3-flash) — stream cost_info',
        async action(ctx) {
          const start = performance.now()
          const costsBefore = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Costs before: ${JSON.stringify(costsBefore)?.slice(0, 150)}`)

          const { text, costInfo, eventCount } = await collectStreamCost(
            ctx.voiceChatId, 'Say hi in one word.', 'gemini-3-flash',
          )
          const responseTime = performance.now() - start
          console.log(`  Response: "${text.slice(0, 80)}" (${eventCount} events, ${(responseTime / 1000).toFixed(1)}s)`)
          console.log(`  Inline cost_info: ${JSON.stringify(costInfo)?.slice(0, 200)}`)

          const costsAfter = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Costs after: ${JSON.stringify(costsAfter)?.slice(0, 150)}`)

          return {
            success: text.length > 0,
            detail: costInfo
              ? `Response: "${text.slice(0, 60)}" | cost_info present`
              : `Response: "${text.slice(0, 60)}" | NO cost_info in stream`,
            costInfo,
            costDelta: costsAfter,
            responseTime,
          }
        },
      },
      {
        label: 'Chat (openai-premium) — stream cost_info',
        async action(ctx) {
          const start = performance.now()
          const { text, costInfo, eventCount } = await collectStreamCost(
            ctx.voiceChatId, 'Say hello in one word.', 'openai-premium',
          )
          const responseTime = performance.now() - start
          console.log(`  Response: "${text.slice(0, 80)}" (${eventCount} events, ${(responseTime / 1000).toFixed(1)}s)`)
          console.log(`  Inline cost_info: ${JSON.stringify(costInfo)?.slice(0, 200)}`)

          return {
            success: text.length > 0,
            detail: costInfo
              ? `Response: "${text.slice(0, 60)}" | cost_info present`
              : `Response: "${text.slice(0, 60)}" | NO cost_info`,
            costInfo,
            responseTime,
          }
        },
      },
      {
        label: 'Chat (claude-sonnet) — stream cost_info',
        async action(ctx) {
          const start = performance.now()
          const { text, costInfo, eventCount } = await collectStreamCost(
            ctx.voiceChatId, 'Say hey in one word.', 'claude-sonnet-4-5-20250514',
          )
          const responseTime = performance.now() - start
          console.log(`  Response: "${text.slice(0, 80)}" (${eventCount} events, ${(responseTime / 1000).toFixed(1)}s)`)
          console.log(`  Inline cost_info: ${JSON.stringify(costInfo)?.slice(0, 200)}`)

          return {
            success: text.length > 0,
            detail: costInfo
              ? `Response: "${text.slice(0, 60)}" | cost_info present`
              : `Response: "${text.slice(0, 60)}" | NO cost_info`,
            costInfo,
            responseTime,
          }
        },
      },
    ],
  }
}

function completionsCostTests(): CostTestSet {
  return {
    name: 'Completions Costs',
    description: 'Verify cost_info from /completions (non-streaming)',
    steps: [
      {
        label: 'Completions (gemini-3-flash)',
        async action(ctx) {
          const result = await callCompletions(ctx.voiceChatId, 'gemini-3-flash', 'Say hi.')
          console.log(`  Success: ${result.success}, cost_info: ${JSON.stringify(result.costInfo)?.slice(0, 200)}`)
          console.log(`  Usage: ${JSON.stringify(result.usage)?.slice(0, 200)}`)

          return {
            success: result.success,
            detail: result.costInfo
              ? `cost_info present | usage: ${JSON.stringify(result.usage)?.slice(0, 100)}`
              : `NO cost_info | usage: ${JSON.stringify(result.usage)?.slice(0, 100)}`,
            costInfo: result.costInfo,
            responseTime: result.responseTime,
          }
        },
      },
      {
        label: 'Completions (openai-premium)',
        async action(ctx) {
          const result = await callCompletions(ctx.voiceChatId, 'openai-premium', 'Say hi.')
          console.log(`  Success: ${result.success}, cost_info: ${JSON.stringify(result.costInfo)?.slice(0, 200)}`)

          return {
            success: result.success,
            detail: result.costInfo ? 'cost_info present' : 'NO cost_info',
            costInfo: result.costInfo,
            responseTime: result.responseTime,
          }
        },
      },
    ],
  }
}

function ttsCostTests(): CostTestSet {
  return {
    name: 'TTS Costs',
    description: 'Verify cost tracking for /tts (cost via session delta)',
    steps: [
      {
        label: 'TTS (google) — session cost delta',
        async action(ctx) {
          const costsBefore = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Costs before: ${JSON.stringify(costsBefore)?.slice(0, 150)}`)

          const result = await callTts(ctx.voiceChatId, 'Hello, this is a cost tracking test.')
          console.log(`  TTS result: success=${result.success}, audioBytes=${result.audioBytes}, time=${(result.responseTime / 1000).toFixed(1)}s${result.error ? `, error: ${result.error}` : ''}`)

          const costsAfter = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Costs after: ${JSON.stringify(costsAfter)?.slice(0, 150)}`)

          return {
            success: result.success,
            detail: result.success
              ? `${result.audioBytes} bytes audio received`
              : `TTS failed: ${result.error || 'unknown error'}`,
            costDelta: costsAfter,
            responseTime: result.responseTime,
          }
        },
      },
    ],
  }
}

function webSearchCostTests(): CostTestSet {
  return {
    name: 'Web Search Costs',
    description: 'Verify cost_info from /web_search',
    steps: [
      {
        label: 'Web search (google_search)',
        async action(ctx) {
          const result = await callWebSearch(ctx.voiceChatId, 'What is the capital of Finland?')
          console.log(`  Success: ${result.success}, cost_info: ${JSON.stringify(result.costInfo)?.slice(0, 200)}`)

          return {
            success: result.success,
            detail: result.costInfo ? 'cost_info present' : (result.success ? 'No cost_info in response' : 'Call failed'),
            costInfo: result.costInfo,
            responseTime: result.responseTime,
          }
        },
      },
    ],
  }
}

function memoryUpdateCostTests(): CostTestSet {
  return {
    name: 'Memory Update Costs',
    description: 'Verify cost tracking for /voice_chat/update_memory',
    steps: [
      {
        label: 'Update memory — cost field in response',
        async action(ctx) {
          const costsBefore = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Costs before: ${JSON.stringify(costsBefore)?.slice(0, 150)}`)

          const result = await callUpdateMemory(ctx.voiceChatId)
          console.log(`  Memory update: success=${result.success}, cost=$${result.cost}, time=${(result.responseTime / 1000).toFixed(1)}s`)

          const costsAfter = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Costs after: ${JSON.stringify(costsAfter)?.slice(0, 150)}`)

          return {
            success: result.success,
            detail: result.cost > 0
              ? `Cost: $${result.cost}`
              : (result.success ? 'Success but cost=0' : 'Memory update failed'),
            costDelta: costsAfter,
            responseTime: result.responseTime,
          }
        },
      },
    ],
  }
}

function sessionCostSummaryTests(): CostTestSet {
  return {
    name: 'Session Cost Summary',
    description: 'Verify /costs/session/{id}/costs returns cumulative data',
    steps: [
      {
        label: 'Generate activity — chat message',
        async action(ctx) {
          const { text, costInfo } = await collectStreamCost(
            ctx.voiceChatId, 'What is 2+2?', 'gemini-3-flash',
          )
          console.log(`  Chat response: "${text.slice(0, 60)}", cost_info: ${!!costInfo}`)

          return {
            success: text.length > 0,
            detail: `Response: "${text.slice(0, 60)}"`,
            costInfo,
          }
        },
      },
      {
        label: 'Get cumulative session costs',
        async action(ctx) {
          const costs = await getSessionCosts(ctx.voiceChatId)
          console.log(`  Session costs: ${JSON.stringify(costs)?.slice(0, 300)}`)

          if (!costs) {
            return { success: false, detail: 'No cost data returned from /costs/session endpoint' }
          }

          // Check for expected fields
          const fields = Object.keys(costs)
          console.log(`  Cost fields: ${fields.join(', ')}`)

          return {
            success: true,
            detail: `Session costs: ${JSON.stringify(costs).slice(0, 200)}`,
            costInfo: costs,
          }
        },
      },
    ],
  }
}

// ── Registry ───────────────────────────────────────────────────

const COST_TEST_SETS: CostTestSet[] = [
  chatCostTests(),
  completionsCostTests(),
  ttsCostTests(),
  webSearchCostTests(),
  memoryUpdateCostTests(),
  sessionCostSummaryTests(),
]

export function getCostTestSets(): { name: string; description: string }[] {
  return COST_TEST_SETS.map(s => ({ name: s.name, description: s.description }))
}

// ── Execution ──────────────────────────────────────────────────

async function runCostSteps(
  ctx: CostTestContext,
  setName: string,
  steps: CostTestStep[],
  onProgress?: CostTestProgressCallback,
): Promise<{ stepResults: CostTestResult['steps']; allPassed: boolean }> {
  const stepResults: CostTestResult['steps'] = []
  let allPassed = true

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepStart = performance.now()
    onProgress?.(setName, i, steps.length, 'running', step.label)

    console.log(`\n--- [${setName}] Step ${i + 1}/${steps.length}: ${step.label} ---`)

    try {
      const outcome = await step.action(ctx)
      const duration = performance.now() - stepStart

      console.log(`  ${outcome.success ? 'PASS' : 'FAIL'} (${(duration / 1000).toFixed(1)}s): ${outcome.detail.slice(0, 150)}`)

      // We consider a step "passed" if the endpoint call succeeded.
      // Missing cost_info is logged but not a hard failure (some endpoints may not track).
      const stepPassed = outcome.success
      if (!stepPassed) allPassed = false

      stepResults.push({
        label: step.label,
        passed: stepPassed,
        detail: outcome.detail,
        costInfo: outcome.costInfo,
        costDelta: outcome.costDelta,
        responseTime: outcome.responseTime,
        duration,
      })

      onProgress?.(setName, i, steps.length, stepPassed ? 'passed' : 'failed', outcome.detail)
    } catch (e) {
      const duration = performance.now() - stepStart
      const error = `Exception: ${e}`
      console.error(`  FAIL — ${error}`)
      allPassed = false

      stepResults.push({
        label: step.label,
        passed: false,
        detail: error,
        duration,
      })

      onProgress?.(setName, i, steps.length, 'failed', error)
    }
  }

  return { stepResults, allPassed }
}

/**
 * Run a single cost test set with its own session.
 */
export async function runCostTestSet(
  setName: string,
  personaId: string,
  onProgress?: CostTestProgressCallback,
): Promise<CostTestResult> {
  const testSet = COST_TEST_SETS.find(s => s.name === setName)
  if (!testSet) {
    return { name: setName, passed: false, steps: [], duration: 0, error: 'Test set not found' }
  }

  const totalStart = performance.now()

  console.log(`\n${'='.repeat(60)}`)
  console.log(`COST TEST: ${setName}`)
  console.log(`${'='.repeat(60)}`)

  let voiceChatId: string
  try {
    const startRes = await startChat({ personaId })
    voiceChatId = startRes.voice_chat_id
    console.log(`Session started: ${voiceChatId}`)
  } catch (e) {
    const error = `Failed to start session: ${e}`
    console.error(error)
    return { name: setName, passed: false, steps: [], duration: performance.now() - totalStart, error }
  }

  const ctx: CostTestContext = { voiceChatId, apiBase: API_BASE }
  const { stepResults, allPassed } = await runCostSteps(ctx, setName, testSet.steps, onProgress)

  // Get final session costs
  const sessionCosts = await getSessionCosts(voiceChatId)
  console.log(`\nFinal session costs: ${JSON.stringify(sessionCosts)?.slice(0, 300)}`)

  try {
    await stopChat(voiceChatId, personaId)
    console.log(`Session stopped: ${voiceChatId}`)
  } catch (e) {
    console.warn(`Failed to stop session: ${e}`)
  }

  const totalDuration = performance.now() - totalStart
  console.log(`\n${setName}: ${allPassed ? 'ALL PASSED' : 'SOME FAILED'} (${(totalDuration / 1000).toFixed(1)}s)`)
  console.log('='.repeat(60))

  return { name: setName, passed: allPassed, steps: stepResults, duration: totalDuration, sessionCosts }
}

/**
 * Run multiple cost test sets in a single shared session.
 */
export async function runAllCostTests(
  personaId: string,
  selectedSets: string[],
  onProgress?: CostTestProgressCallback,
): Promise<CostTestResult[]> {
  const totalStart = performance.now()

  console.log(`\n${'='.repeat(60)}`)
  console.log(`COST TEST RUN: ${selectedSets.length} set(s) in a single session`)
  console.log(`${'='.repeat(60)}`)

  let voiceChatId: string
  try {
    const startRes = await startChat({ personaId })
    voiceChatId = startRes.voice_chat_id
    console.log(`Session started: ${voiceChatId}`)
  } catch (e) {
    const error = `Failed to start session: ${e}`
    console.error(error)
    return selectedSets.map(name => ({
      name, passed: false, steps: [], duration: 0, error,
    }))
  }

  const ctx: CostTestContext = { voiceChatId, apiBase: API_BASE }
  const results: CostTestResult[] = []

  for (const setName of selectedSets) {
    const testSet = COST_TEST_SETS.find(s => s.name === setName)
    if (!testSet) {
      results.push({ name: setName, passed: false, steps: [], duration: 0, error: 'Test set not found' })
      continue
    }

    const setStart = performance.now()
    console.log(`\n${'─'.repeat(40)}`)
    console.log(`SET: ${setName}`)
    console.log(`${'─'.repeat(40)}`)

    const { stepResults, allPassed } = await runCostSteps(ctx, setName, testSet.steps, onProgress)
    const setDuration = performance.now() - setStart

    console.log(`\n${setName}: ${allPassed ? 'ALL PASSED' : 'SOME FAILED'} (${(setDuration / 1000).toFixed(1)}s)`)
    results.push({ name: setName, passed: allPassed, steps: stepResults, duration: setDuration })
  }

  // Final session costs
  const sessionCosts = await getSessionCosts(voiceChatId)
  console.log(`\nFinal cumulative session costs: ${JSON.stringify(sessionCosts)?.slice(0, 300)}`)

  try {
    await stopChat(voiceChatId, personaId)
    console.log(`Session stopped: ${voiceChatId}`)
  } catch (e) {
    console.warn(`Failed to stop session: ${e}`)
  }

  const totalDuration = performance.now() - totalStart
  const passed = results.filter(r => r.passed).length
  console.log(`\n${'='.repeat(60)}`)
  console.log(`ALL DONE: ${passed}/${results.length} sets passed (${(totalDuration / 1000).toFixed(1)}s total)`)
  console.log('='.repeat(60))

  return results
}
